import React, { useCallback, useEffect, useMemo, useState } from "react";
import Select from "react-select";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { useAuthFetch } from "../hooks/useAuthFetch";
import ConfirmModal from "../utils/ConfirmModal";
import "../stili/AggiornaMagazzino.css";

const API_URL = process.env.REACT_APP_API_URL;

const categorieOptions = [
  { value: "", label: "Tutte le categorie" },
  { value: "KIT_GARA", label: "Kit gara" },
  { value: "KIT_ALLENAMENTO", label: "Kit allenamento" },
  { value: "ABBIGLIAMENTO", label: "Abbigliamento" },
  { value: "BORSA", label: "Borsa" },
  { value: "GIUBBOTTO", label: "Giubbotto" },
  { value: "CALZE", label: "Calze" },
  { value: "PARASTINCHI", label: "Parastinchi" },
  { value: "ACCESSORIO", label: "Accessorio" },
  { value: "ALTRO", label: "Altro" }
];

const statoOptions = [
  { value: "", label: "Tutti gli stati" },
  { value: "ATTIVO", label: "Attivo" },
  { value: "NON_ATTIVO", label: "Non attivo" }
];

const statoEditOptions = [
  { value: "ATTIVO", label: "Attivo" },
  { value: "NON_ATTIVO", label: "Non attivo" }
];

const selectStyles = {
  control: (base, state) => ({
    ...base,
    minHeight: 44,
    borderRadius: 14,
    background: "rgba(255,255,255,0.12)",
    borderColor: state.isFocused ? "rgba(255,255,255,0.45)" : "rgba(255,255,255,0.18)",
    boxShadow: "none",
    backdropFilter: "blur(6px)"
  }),
  menu: (base) => ({
    ...base,
    borderRadius: 14,
    overflow: "hidden",
    background: "rgba(18, 31, 70, 0.96)",
    backdropFilter: "blur(10px)",
    zIndex: 9999
  }),
  menuPortal: (base) => ({
    ...base,
    zIndex: 9999
  }),
  singleValue: (base) => ({
    ...base,
    color: "#fff"
  }),
  input: (base) => ({
    ...base,
    color: "#fff"
  }),
  placeholder: (base) => ({
    ...base,
    color: "rgba(255,255,255,0.70)"
  }),
  option: (base, state) => ({
    ...base,
    background: state.isFocused ? "rgba(255,255,255,0.14)" : "transparent",
    color: "#fff",
    cursor: "pointer"
  })
};

const formatEuro = (value) => {
  const num = Number(value || 0);
  return num.toLocaleString("it-IT", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  });
};

const getCategoriaLabel = (value) => {
  return categorieOptions.find((opt) => opt.value === value)?.label || value || "-";
};

const createInitialMovimento = () => ({
  quantita: "",
  causale: "",
  note: ""
});

const parseDecimal = (value) => {
  if (value === null || value === undefined || value === "") return null;
  const parsed = Number(String(value).replace(",", ".").trim());
  return Number.isNaN(parsed) ? null : parsed;
};

const parseInteger = (value) => {
  if (value === null || value === undefined || value === "") return null;
  const parsed = Number(value);
  return Number.isInteger(parsed) ? parsed : null;
};

export default function AggiornaMagazzino() {
  const authFetch = useAuthFetch();
  const navigate = useNavigate();

  const [articoli, setArticoli] = useState([]);
  const [loading, setLoading] = useState(true);

  const [filtroTesto, setFiltroTesto] = useState("");
  const [filtroCategoria, setFiltroCategoria] = useState("");
  const [filtroStato, setFiltroStato] = useState("");
  const [soloScortaBassa, setSoloScortaBassa] = useState(false);

  const [articoloInModifica, setArticoloInModifica] = useState(null);
  const [formModifica, setFormModifica] = useState(null);
  const [savingEdit, setSavingEdit] = useState(false);

  const [modalCaricoOpen, setModalCaricoOpen] = useState(false);
  const [modalRettificaOpen, setModalRettificaOpen] = useState(false);
  const [articoloMovimento, setArticoloMovimento] = useState(null);
  const [movimentoForm, setMovimentoForm] = useState(createInitialMovimento());
  const [savingMovimento, setSavingMovimento] = useState(false);

  const fetchArticoli = useCallback(async () => {
    setLoading(true);
    try {
      const res = await authFetch(`${API_URL}/api/admin/magazzino/articoli`);
      if (!res.ok) {
        throw new Error("Errore nel caricamento degli articoli di magazzino");
      }

      const data = await res.json();
      setArticoli(Array.isArray(data) ? data : []);
    } catch (err) {
      toast.error(err.message || "Errore nel caricamento articoli");
      setArticoli([]);
    } finally {
      setLoading(false);
    }
  }, [authFetch]);

  useEffect(() => {
    fetchArticoli();
  }, [fetchArticoli]);

  const articoliFiltrati = useMemo(() => {
    const testo = filtroTesto.trim().toLowerCase();

    return articoli.filter((art) => {
      const matchTesto =
        !testo ||
        String(art.nome || "").toLowerCase().includes(testo) ||
        String(art.codice || "").toLowerCase().includes(testo) ||
        String(art.taglia || "").toLowerCase().includes(testo);

      const matchCategoria = !filtroCategoria || art.categoriaArticolo === filtroCategoria;
      const matchStato = !filtroStato || art.stato === filtroStato;
      const matchScorta = !soloScortaBassa || art.scortaBassa === true;

      return matchTesto && matchCategoria && matchStato && matchScorta;
    });
  }, [articoli, filtroTesto, filtroCategoria, filtroStato, soloScortaBassa]);

  const statistiche = useMemo(() => {
    const articoliAttivi = articoli.filter((a) => a.stato === "ATTIVO").length;
    const pezziTotali = articoli.reduce((acc, a) => acc + Number(a.quantitaDisponibile || 0), 0);
    const scorteBasse = articoli.filter((a) => a.scortaBassa === true).length;
    const valoreStimato = articoli.reduce(
      (acc, a) => acc + Number(a.quantitaDisponibile || 0) * Number(a.prezzoVendita || 0),
      0
    );

    return {
      articoliAttivi,
      pezziTotali,
      scorteBasse,
      valoreStimato
    };
  }, [articoli]);

  const apriModifica = (articolo) => {
    setArticoloInModifica(articolo.id);
    setFormModifica({
      codice: articolo.codice || "",
      nome: articolo.nome || "",
      descrizione: articolo.descrizione || "",
      categoriaArticolo: articolo.categoriaArticolo || "",
      taglia: articolo.taglia || "",
      prezzoAcquisto: articolo.prezzoAcquisto ?? "",
      prezzoVendita: articolo.prezzoVendita ?? "",
      scortaMinima: articolo.scortaMinima ?? "",
      stato: articolo.stato || "ATTIVO",
      note: articolo.note || ""
    });
  };

  const annullaModifica = () => {
    setArticoloInModifica(null);
    setFormModifica(null);
  };

  const salvaModifica = async (articolo) => {
    if (!formModifica) return;

    const prezzoAcquisto = parseDecimal(formModifica.prezzoAcquisto);
    const prezzoVendita = parseDecimal(formModifica.prezzoVendita);
    const scortaMinima = parseInteger(formModifica.scortaMinima);

    if (!formModifica.codice.trim()) {
      toast.error("Inserisci il codice articolo");
      return;
    }

    if (!formModifica.nome.trim()) {
      toast.error("Inserisci il nome articolo");
      return;
    }

    if (!formModifica.categoriaArticolo) {
      toast.error("Seleziona la categoria articolo");
      return;
    }

    if (prezzoVendita === null || prezzoVendita < 0) {
      toast.error("Inserisci un prezzo vendita valido");
      return;
    }

    if (scortaMinima === null || scortaMinima < 0) {
      toast.error("Inserisci una scorta minima valida");
      return;
    }

    if (prezzoAcquisto !== null && prezzoAcquisto < 0) {
      toast.error("Il prezzo acquisto non può essere negativo");
      return;
    }

    setSavingEdit(true);

    try {
      const payload = {
        codice: formModifica.codice.trim(),
        nome: formModifica.nome.trim(),
        descrizione: formModifica.descrizione.trim() || null,
        categoriaArticolo: formModifica.categoriaArticolo,
        taglia: formModifica.taglia.trim() || null,
        prezzoAcquisto,
        prezzoVendita,
        scortaMinima,
        stato: formModifica.stato,
        note: formModifica.note.trim() || null
      };

      const res = await authFetch(`${API_URL}/api/admin/magazzino/articoli/${articolo.id}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify(payload)
      });

      if (!res.ok) {
        const msg = await res.text();
        throw new Error(msg || "Errore durante l'aggiornamento articolo");
      }

      toast.success("Articolo aggiornato correttamente");
      annullaModifica();
      await fetchArticoli();
    } catch (err) {
      toast.error(err.message || "Errore durante il salvataggio");
    } finally {
      setSavingEdit(false);
    }
  };

  const apriCarico = (articolo) => {
    setArticoloMovimento(articolo);
    setMovimentoForm(createInitialMovimento());
    setModalCaricoOpen(true);
  };

  const apriRettifica = (articolo) => {
    setArticoloMovimento(articolo);
    setMovimentoForm(createInitialMovimento());
    setModalRettificaOpen(true);
  };

  const chiudiModaliMovimento = () => {
    if (savingMovimento) return;

    setModalCaricoOpen(false);
    setModalRettificaOpen(false);
    setArticoloMovimento(null);
    setMovimentoForm(createInitialMovimento());
  };

  const salvaCarico = async () => {
    if (!articoloMovimento) return;

    const quantita = parseInteger(movimentoForm.quantita);

    if (quantita === null || quantita <= 0) {
      toast.error("Inserisci una quantità valida per il carico");
      return;
    }

    setSavingMovimento(true);

    try {
      const payload = {
        idArticolo: articoloMovimento.id,
        quantita,
        causale: movimentoForm.causale?.trim() || "Carico manuale magazzino",
        note: movimentoForm.note?.trim() || null
      };

      const res = await authFetch(`${API_URL}/api/admin/magazzino/movimenti/carico`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify(payload)
      });

      if (!res.ok) {
        const msg = await res.text();
        throw new Error(msg || "Errore durante il carico di magazzino");
      }

      toast.success("Carico registrato correttamente");
      chiudiModaliMovimento();
      await fetchArticoli();
    } catch (err) {
      toast.error(err.message || "Errore durante il carico");
    } finally {
      setSavingMovimento(false);
    }
  };

  const salvaRettifica = async () => {
    if (!articoloMovimento) return;

    const quantita = parseInteger(movimentoForm.quantita);

    if (quantita === null || quantita === 0) {
      toast.error("Inserisci una quantità valida per la rettifica");
      return;
    }

    setSavingMovimento(true);

    try {
      const payload = {
        quantita,
        causale: movimentoForm.causale?.trim() || "Rettifica manuale magazzino",
        note: movimentoForm.note?.trim() || null
      };

      const res = await authFetch(
        `${API_URL}/api/admin/magazzino/movimenti/rettifica/${articoloMovimento.id}`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json"
          },
          body: JSON.stringify(payload)
        }
      );

      if (!res.ok) {
        const msg = await res.text();
        throw new Error(msg || "Errore durante la rettifica di magazzino");
      }

      toast.success("Rettifica registrata correttamente");
      chiudiModaliMovimento();
      await fetchArticoli();
    } catch (err) {
      toast.error(err.message || "Errore durante la rettifica");
    } finally {
      setSavingMovimento(false);
    }
  };

  return (
    <div className="sfondo-immagine">
      <div className="aggiorna-magazzino-page">
        <div className="aggiorna-magazzino-container">
          <div className="aggiorna-magazzino-header card-glass">
            <div className="aggiorna-magazzino-header-top">
              <div>
                <h2 className="aggiorna-magazzino-title">Gestione Magazzino</h2>
                <p className="aggiorna-magazzino-subtitle">
                  Monitora articoli, giacenze, soglie minime e movimenti di carico o rettifica.
                </p>
              </div>

              <button
                type="button"
                className="btn-secondario"
                onClick={() => navigate("/gestionale")}
              >
                Torna al gestionale
              </button>
            </div>
          </div>

          <div className="aggiorna-magazzino-stats">
            <div className="card-glass stat-card">
              <span>Articoli attivi</span>
              <strong>{statistiche.articoliAttivi}</strong>
            </div>
            <div className="card-glass stat-card">
              <span>Pezzi disponibili</span>
              <strong>{statistiche.pezziTotali}</strong>
            </div>
            <div className="card-glass stat-card stat-card-alert">
              <span>Scorte basse</span>
              <strong>{statistiche.scorteBasse}</strong>
            </div>
            <div className="card-glass stat-card">
              <span>Valore stimato</span>
              <strong>{formatEuro(statistiche.valoreStimato)} €</strong>
            </div>
          </div>

          <div className="card-glass aggiorna-magazzino-filtri">
            <div className="aggiorna-magazzino-filtri-grid">
              <div className="aggiorna-magazzino-field">
                <label>Ricerca</label>
                <input
                  type="text"
                  value={filtroTesto}
                  onChange={(e) => setFiltroTesto(e.target.value)}
                  placeholder="Codice, nome o taglia"
                />
              </div>

              <div className="aggiorna-magazzino-field">
                <label>Categoria</label>
                <Select
                  options={categorieOptions}
                  value={categorieOptions.find((opt) => opt.value === filtroCategoria) || categorieOptions[0]}
                  onChange={(opt) => setFiltroCategoria(opt?.value || "")}
                  styles={selectStyles}
                  isClearable={false}
                  menuPortalTarget={document.body}
                  menuPosition="fixed"
                />
              </div>

              <div className="aggiorna-magazzino-field">
                <label>Stato</label>
                <Select
                  options={statoOptions}
                  value={statoOptions.find((opt) => opt.value === filtroStato) || statoOptions[0]}
                  onChange={(opt) => setFiltroStato(opt?.value || "")}
                  styles={selectStyles}
                  isClearable={false}
                  menuPortalTarget={document.body}
                  menuPosition="fixed"
                />
              </div>

              <div className="aggiorna-magazzino-check">
                <label className="check-inline">
                  <input
                    type="checkbox"
                    checked={soloScortaBassa}
                    onChange={(e) => setSoloScortaBassa(e.target.checked)}
                  />
                  Mostra solo scorte basse
                </label>
              </div>
            </div>
          </div>

          <div className="aggiorna-magazzino-list">
            {loading ? (
              <div className="card-glass empty-state">Caricamento articoli di magazzino...</div>
            ) : articoliFiltrati.length === 0 ? (
              <div className="card-glass empty-state">Nessun articolo trovato con i filtri selezionati.</div>
            ) : (
              articoliFiltrati.map((articolo) => {
                const isEditing = articoloInModifica === articolo.id;

                return (
                  <div
                    key={articolo.id}
                    className={`card-glass articolo-card ${articolo.scortaBassa ? "articolo-card-alert" : ""}`}
                  >
                    {!isEditing ? (
                      <>
                        <div className="articolo-card-top">
                          <div>
                            <h3>{articolo.nome}</h3>
                            <p className="articolo-card-meta">
                              Codice: <strong>{articolo.codice}</strong> · Categoria:{" "}
                              <strong>{getCategoriaLabel(articolo.categoriaArticolo)}</strong> · Taglia:{" "}
                              <strong>{articolo.taglia || "-"}</strong>
                            </p>
                          </div>

                          <div className="articolo-badges">
                            <span className={`badge-stato ${articolo.stato === "ATTIVO" ? "ok" : "off"}`}>
                              {articolo.stato === "ATTIVO" ? "Attivo" : "Non attivo"}
                            </span>

                            {articolo.scortaBassa && <span className="badge-alert">Scorta bassa</span>}
                          </div>
                        </div>

                        <div className="articolo-card-body">
                          <div className="articolo-info-grid">
                            <div>
                              <span>Prezzo acquisto</span>
                              <strong>{formatEuro(articolo.prezzoAcquisto)} €</strong>
                            </div>
                            <div>
                              <span>Prezzo vendita</span>
                              <strong>{formatEuro(articolo.prezzoVendita)} €</strong>
                            </div>
                            <div>
                              <span>Disponibilità</span>
                              <strong>{articolo.quantitaDisponibile}</strong>
                            </div>
                            <div>
                              <span>Scorta minima</span>
                              <strong>{articolo.scortaMinima}</strong>
                            </div>
                          </div>

                          {(articolo.descrizione || articolo.note) && (
                            <div className="articolo-note-box">
                              {articolo.descrizione && (
                                <p>
                                  <strong>Descrizione:</strong> {articolo.descrizione}
                                </p>
                              )}
                              {articolo.note && (
                                <p>
                                  <strong>Note:</strong> {articolo.note}
                                </p>
                              )}
                            </div>
                          )}
                        </div>

                        <div className="articolo-card-actions">
                          <button className="btn-secondario" onClick={() => apriModifica(articolo)}>
                            Modifica
                          </button>
                          <button className="btn-secondario" onClick={() => apriCarico(articolo)}>
                            Carico
                          </button>
                          <button className="btn-secondario" onClick={() => apriRettifica(articolo)}>
                            Rettifica
                          </button>
                        </div>
                      </>
                    ) : (
                      <>
                        <div className="articolo-card-top">
                          <div>
                            <h3>Modifica articolo</h3>
                            <p className="articolo-card-meta">
                              Stai aggiornando <strong>{articolo.nome}</strong>
                            </p>
                          </div>
                        </div>

                        <div className="articolo-edit-grid">
                          <div className="aggiorna-magazzino-field">
                            <label>Codice</label>
                            <input
                              type="text"
                              value={formModifica?.codice || ""}
                              onChange={(e) =>
                                setFormModifica((prev) => ({ ...prev, codice: e.target.value }))
                              }
                            />
                          </div>

                          <div className="aggiorna-magazzino-field">
                            <label>Nome</label>
                            <input
                              type="text"
                              value={formModifica?.nome || ""}
                              onChange={(e) =>
                                setFormModifica((prev) => ({ ...prev, nome: e.target.value }))
                              }
                            />
                          </div>

                          <div className="aggiorna-magazzino-field">
                            <label>Categoria</label>
                            <Select
                              options={categorieOptions.filter((opt) => opt.value)}
                              value={
                                categorieOptions
                                  .filter((opt) => opt.value)
                                  .find((opt) => opt.value === formModifica?.categoriaArticolo) || null
                              }
                              onChange={(opt) =>
                                setFormModifica((prev) => ({
                                  ...prev,
                                  categoriaArticolo: opt?.value || ""
                                }))
                              }
                              styles={selectStyles}
                              menuPortalTarget={document.body}
                              menuPosition="fixed"
                            />
                          </div>

                          <div className="aggiorna-magazzino-field">
                            <label>Taglia</label>
                            <input
                              type="text"
                              value={formModifica?.taglia || ""}
                              onChange={(e) =>
                                setFormModifica((prev) => ({ ...prev, taglia: e.target.value }))
                              }
                            />
                          </div>

                          <div className="aggiorna-magazzino-field">
                            <label>Prezzo acquisto</label>
                            <input
                              type="number"
                              step="0.01"
                              min="0"
                              value={formModifica?.prezzoAcquisto ?? ""}
                              onChange={(e) =>
                                setFormModifica((prev) => ({
                                  ...prev,
                                  prezzoAcquisto: e.target.value
                                }))
                              }
                            />
                          </div>

                          <div className="aggiorna-magazzino-field">
                            <label>Prezzo vendita</label>
                            <input
                              type="number"
                              step="0.01"
                              min="0"
                              value={formModifica?.prezzoVendita ?? ""}
                              onChange={(e) =>
                                setFormModifica((prev) => ({
                                  ...prev,
                                  prezzoVendita: e.target.value
                                }))
                              }
                            />
                          </div>

                          <div className="aggiorna-magazzino-field">
                            <label>Scorta minima</label>
                            <input
                              type="number"
                              min="0"
                              value={formModifica?.scortaMinima ?? ""}
                              onChange={(e) =>
                                setFormModifica((prev) => ({
                                  ...prev,
                                  scortaMinima: e.target.value
                                }))
                              }
                            />
                          </div>

                          <div className="aggiorna-magazzino-field">
                            <label>Stato</label>
                            <Select
                              options={statoEditOptions}
                              value={
                                statoEditOptions.find((opt) => opt.value === formModifica?.stato) || null
                              }
                              onChange={(opt) =>
                                setFormModifica((prev) => ({
                                  ...prev,
                                  stato: opt?.value || "ATTIVO"
                                }))
                              }
                              styles={selectStyles}
                              isClearable={false}
                              menuPortalTarget={document.body}
                              menuPosition="fixed"
                            />
                          </div>

                          <div className="aggiorna-magazzino-field articolo-edit-full">
                            <label>Descrizione</label>
                            <textarea
                              rows="3"
                              value={formModifica?.descrizione || ""}
                              onChange={(e) =>
                                setFormModifica((prev) => ({
                                  ...prev,
                                  descrizione: e.target.value
                                }))
                              }
                            />
                          </div>

                          <div className="aggiorna-magazzino-field articolo-edit-full">
                            <label>Note</label>
                            <input
                              type="text"
                              value={formModifica?.note || ""}
                              onChange={(e) =>
                                setFormModifica((prev) => ({
                                  ...prev,
                                  note: e.target.value
                                }))
                              }
                            />
                          </div>
                        </div>

                        <div className="articolo-card-actions">
                          <button className="btn-secondario" onClick={annullaModifica} disabled={savingEdit}>
                            Annulla
                          </button>
                          <button
                            className="btn-primario"
                            onClick={() => salvaModifica(articolo)}
                            disabled={savingEdit}
                          >
                            {savingEdit ? "Salvataggio..." : "Salva modifiche"}
                          </button>
                        </div>
                      </>
                    )}
                  </div>
                );
              })
            )}
          </div>
        </div>
      </div>

      <ConfirmModal
  open={modalCaricoOpen}
  title="Registra carico magazzino"
  message={
    articoloMovimento ? (
      <div className="modal-movimento-body">
        <p>
          Articolo: <strong>{articoloMovimento.nome}</strong>
        </p>
        <p>
          Disponibilità attuale: <strong>{articoloMovimento.quantitaDisponibile}</strong>
        </p>

        <div className="aggiorna-magazzino-field">
          <label>Quantità da caricare</label>
          <input
            type="number"
            min="1"
            value={movimentoForm.quantita}
            onChange={(e) =>
              setMovimentoForm((prev) => ({ ...prev, quantita: e.target.value }))
            }
          />
        </div>

        <div className="aggiorna-magazzino-field">
          <label>Causale</label>
          <input
            type="text"
            value={movimentoForm.causale}
            onChange={(e) =>
              setMovimentoForm((prev) => ({ ...prev, causale: e.target.value }))
            }
            placeholder="Nuovo rifornimento"
          />
        </div>

        <div className="aggiorna-magazzino-field">
          <label>Note</label>
          <input
            type="text"
            value={movimentoForm.note}
            onChange={(e) =>
              setMovimentoForm((prev) => ({ ...prev, note: e.target.value }))
            }
            placeholder="Note facoltative"
          />
        </div>
      </div>
    ) : null
  }
  confirmText={savingMovimento ? "Salvataggio..." : "Conferma carico"}
  cancelText="Chiudi"
  onConfirm={salvaCarico}
  onCancel={chiudiModaliMovimento}
/>

<ConfirmModal
  open={modalRettificaOpen}
  title="Registra rettifica magazzino"
  message={
    articoloMovimento ? (
      <div className="modal-movimento-body">
        <p>
          Articolo: <strong>{articoloMovimento.nome}</strong>
        </p>
        <p>
          Disponibilità attuale: <strong>{articoloMovimento.quantitaDisponibile}</strong>
        </p>
        <p className="modal-help-text">
          Usa un numero positivo per aumentare lo stock, negativo per diminuirlo.
        </p>

        <div className="aggiorna-magazzino-field">
          <label>Quantità rettifica</label>
          <input
            type="number"
            value={movimentoForm.quantita}
            onChange={(e) =>
              setMovimentoForm((prev) => ({ ...prev, quantita: e.target.value }))
            }
          />
        </div>

        <div className="aggiorna-magazzino-field">
          <label>Causale</label>
          <input
            type="text"
            value={movimentoForm.causale}
            onChange={(e) =>
              setMovimentoForm((prev) => ({ ...prev, causale: e.target.value }))
            }
            placeholder="Correzione inventario"
          />
        </div>

        <div className="aggiorna-magazzino-field">
          <label>Note</label>
          <input
            type="text"
            value={movimentoForm.note}
            onChange={(e) =>
              setMovimentoForm((prev) => ({ ...prev, note: e.target.value }))
            }
            placeholder="Note facoltative"
          />
        </div>
      </div>
    ) : null
  }
  confirmText={savingMovimento ? "Salvataggio..." : "Conferma rettifica"}
  cancelText="Chiudi"
  onConfirm={salvaRettifica}
  onCancel={chiudiModaliMovimento}
/>
    </div>
  );
}